import React, { useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import 'react-phone-number-input/style.css'
import { isValidPhoneNumber } from 'react-phone-number-input'
import Select from 'react-select';
import ReadOnlyInputFields from "../../reusable-components/read-only-input";
import PasswordInput from "../../reusable-components/password-input";
import Button from "../../reusable-components/button";
import TextInput from "../../reusable-components/text-input";
import { cloneDeep, every, filter, find, findIndex, isEmpty } from "lodash";
import { setFormData } from "../../store/slices/Activation/formSlice";
import { useTranslation } from 'react-i18next';
import usePostActivateAccount from "../../hooks/Activation/postAccountActivate";
import { ProfileStatuses } from "../../constants/Activation/enum";
import AgreementCheck from "../../reusable-components/agreement-check";
import PhoneNumberInput from "../../reusable-components/phone-number-input.js";
import PropTypes from 'prop-types'

const ActivationForm = ({ queryParams, setScreen }) =>
{
  const dispatch = useDispatch()
  const { t } = useTranslation();
  const [formLoader, setFormLoader] = useState(false)
  const { userPrefLangList, profileStatus } = useSelector((state) => state.activation);
  const { readOnlyFields, form } = useSelector((state) => state.form);
  const [postActivateAccount] = usePostActivateAccount(setScreen, setFormLoader)

  const readOnlyFieldsInput = readOnlyFields.map((field) =>
    <ReadOnlyInputFields inputValue={field.value} key={field.label} labelValue={field.label} />
  );

  const validateNameLength = (value) =>
  {
    return (value || "").length <= 64
  }

  /**
   * enable disable logic for the submit button
   * @return   {boolean} button disable value
   */
  const handleDisableButton = () =>
  {
    let displayedFields = filter(form, { display: true })
    let mandatoryFields = filter(displayedFields, { mandatory: true })
    let hasMandatoryFieldError = !every(mandatoryFields, e => (!isEmpty(e.value) || e.value === true))
    let hasValidationError = displayedFields.some((field) => field.error)

    return hasMandatoryFieldError || hasValidationError
  }

  /**
   * on Change function all form fields apart from password
   * @param   {string} field  Form field name
   * @param   {string/object/boolean} val Input value
   */
  const handleFormValueChange = (field, val) =>
  {
    const formData = cloneDeep(form)
    let index = findIndex(formData, { field })

    formData[index].value = val

    if (field === "firstName" || field === "lastName")
    {
      const isValidLength = validateNameLength(val)

      if (!isValidLength)
      {
        formData[index].error = true
        formData[index].errorMessage = t('widget.validation.maxCharacterLimit')
      }
      else
      {
        formData[index].error = false
        formData[index].errorMessage = ""
      }
    }
    else
    {
      formData[index].error = false
      formData[index].errorMessage = ""
    }

    dispatch(setFormData(formData))
  }

  /**
   * on Click function for submit button. It will call the user account activation api after validating the phone number
   */
  const handleSubmit = () =>
  {
    const formData = cloneDeep(form)
    let phoneNumberIndex = findIndex(formData, { field: "phoneNumber" })

    if (!formData[phoneNumberIndex].isReadOnly)
    {
      if (!isEmpty(formData[phoneNumberIndex].value))
      {
        if (isValidPhoneNumber(formData[phoneNumberIndex].value))
        {
          formData[phoneNumberIndex].error = false
          formData[phoneNumberIndex].errorMessage = ""
          postActivateAccount(queryParams)
        }
        else
        {
          formData[phoneNumberIndex].error = true
          formData[phoneNumberIndex].errorMessage = t('widget.phoneNumber.invalidNumberMessage')
        }
      }
      else
      {
        postActivateAccount(queryParams)
      }
    }
    else
    {
      postActivateAccount(queryParams)
    }
    dispatch(setFormData(formData))
  }

  return (
    <div className="pagination-view">
      {formLoader && <div id="overlay"></div>}
      <div className="row text-body">
        {profileStatus === ProfileStatuses.VERIFICATION_PENDING ? t('widget.formActivateTitle') : t('widget.formSubTitle')}
      </div>
      <form>
        {readOnlyFieldsInput}
        {find(form, { field: "firstName" })?.display &&
          <TextInput fieldName="firstName" handleChange={handleFormValueChange} />}
        {find(form, { field: "lastName" })?.display &&
          <TextInput fieldName="lastName" handleChange={handleFormValueChange} />}
        {find(form, { field: "phoneNumber" }).display &&
          <PhoneNumberInput handleChange={handleFormValueChange} />}
        {find(form, { field: "password" }).display && <PasswordInput />}
        {find(form, { field: "language" }).display === "true" &&
          <div className="col-md-24 form-group">
            <div className="placeholderContainer">
              <Select
                value={find(form, { field: "language" }).value}
                onChange={(lang) => handleFormValueChange("language", lang)}
                options={userPrefLangList}
                placeholder={`${t('widget.language.languagePlaceholder')} ${t('widget.placeholderOptional')}`}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          </div>}
        {find(form, { field: "agreementCheck" }).display &&
          <AgreementCheck handleChange={handleFormValueChange} />}
        <Button isDisabled={handleDisableButton()} buttonText={t('widget.submitButton')} onClick={handleSubmit} loader={formLoader} />
      </form>
    </div>
  );
};

ActivationForm.propTypes = {
  queryParams: PropTypes.object,
  setScreen: PropTypes.func
};

export default ActivationForm;